import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

/**
 * Les langues servies, et **la direction d'écriture de chacune**.
 *
 * 🔴 LA DIRECTION SUIT LA LANGUE, ELLE N'EST PAS UNE PRÉFÉRENCE À PART. Un
 * lecteur d'hébreu ne choisit pas de lire de droite à gauche : c'est une
 * propriété de sa langue, pas un réglage. *Un sélecteur « sens d'écriture »
 * offrirait de configurer une erreur* — l'hébreu en LTR n'est pas une option,
 * c'est une faute. Le mode clair/sombre, lui, est un vrai choix : d'où deux
 * mécanismes distincts, et non deux réglages côte à côte.
 *
 * ⚠️ UNE SEULE ÉCRITURE DE LA LISTE. Elle était recopiée **trois fois** dans
 * ce fichier — deux fois en conditions `=== 'fr' || === 'en' || === 'he'`, une
 * fois dans le repli — plus une quatrième dans `LanguageSwitcher`. Ajouter
 * l'arabe demandait quatre modifications dont trois invisibles depuis la
 * quatrième.
 */
export const LANGUES = {
  fr: { direction: 'ltr' },
  en: { direction: 'ltr' },
  he: { direction: 'rtl' },
};

export const LANGUE_PAR_DEFAUT = 'fr';

/** La direction d'une langue, avec repli sur le défaut pour une valeur inconnue. */
export const directionDe = (langue) =>
  (LANGUES[langue] || LANGUES[LANGUE_PAR_DEFAUT]).direction;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(LANGUE_PAR_DEFAUT);
  const [translations, setTranslations] = useState({});
  const direction = directionDe(language);

  // Charger les traductions
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const response = await fetch(`/locales/${language}.json`);
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error('Erreur lors du chargement des traductions:', error);
        // Fallback vers le français en cas d'erreur
        if (language !== 'fr') {
          const fallbackResponse = await fetch('/locales/fr.json');
          const fallbackData = await fallbackResponse.json();
          setTranslations(fallbackData);
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Charger la langue depuis localStorage au démarrage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && LANGUES[savedLanguage]) {
      setLanguage(savedLanguage);
    }
  }, []);

  /**
   * 🔴 `<html dir>` ET `<html lang>` SONT POSÉS ICI, ET C'EST LE SEUL ENDROIT.
   *
   * Le thème MUI oriente ce que MUI peint ; **il ne dit rien au NAVIGATEUR**.
   * Or trois choses ne passent pas par MUI et lisent `<html>` directement :
   *   · l'**algorithme bidi** d'Unicode, qui ordonne le texte brut ;
   *   · les **contrôles natifs** — barres de défilement, menus de sélection,
   *     boîtes de dialogue du navigateur ;
   *   · les **lecteurs d'écran**, qui choisissent leur voix sur `lang`.
   *
   * ⚠️ *Sans `dir` sur `<html>`, une interface peut être parfaitement retournée
   * et rester illisible* : le texte y coulerait toujours de gauche à droite à
   * l'intérieur de conteneurs correctement inversés.
   */
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }, [direction, language]);

  const changeLanguage = (newLanguage) => {
    if (LANGUES[newLanguage]) {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
    }
  };

  const t = (key, variables = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Retourner la clé si la traduction n'est pas trouvée
        return key;
      }
    }
    
    if (typeof value !== 'string') {
      return key;
    }
    
    // Interpoler les variables si présentes
    if (Object.keys(variables).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        return variables[varName] !== undefined ? String(variables[varName]) : match;
      });
    }
    
    return value;
  };

  const value = {
    language,
    direction,
    changeLanguage,
    t,
    translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

